import FormField from '@/components/FormField';
import LotCard from '@/components/lot/LotCard';
import MediaUploader from '@/components/lot/MediaUploader';
import { Button } from '@/components/shadcn-ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/shadcn-ui/card';
import { Label } from '@/components/shadcn-ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn-ui/select';
import Loader from '@/components/sharedComponents/Loader';
import { useMediaUploader } from '@/hooks/useMediaUploader';
import { cn } from '@/lib/utils';
import { deleteMedia, getLotById, updateSingleLotProcessStep } from '@/network/api.service';
import { ProcessingStepSchema } from '@/schema/process-lot-schema';
import { useLotStore } from '@/stores/useLot.store';
import { useMasterStore } from '@/stores/useMaster.store';
import { yupResolver } from '@hookform/resolvers/yup';
import { X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

const UpdateProcessStageInformationPage = () => {
  const { id, step_id } = useParams();
  const navigate = useNavigate();
  const agentList = useMasterStore(state => state.agentList);
  const lot = useLotStore(state => state.lot);

  const uploadRef = useRef(null);
  const media = useMediaUploader({ folder: 'lot-process-media' });

  const [mediaToRemove, setMediaToRemove] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isFormReady, setIsFormReady] = useState(false);

  const [agentSpecificList, setAgentSpecificList] = useState([]);
  const [selectedAgentType, setSelectedAgentType] = useState('');
  const [stepData, setStepData] = useState();

  const agentTypeOptions = agentList
    ? Object.keys(agentList).map(key => ({
        label: key,
        value: key,
      }))
    : [];

  const {
    control,
    register,
    reset,
    setValue,
    getValues,
    formState: { errors },
    handleSubmit,
  } = useForm({
    resolver: yupResolver(ProcessingStepSchema),
  });

  const { fields, remove } = useFieldArray({
    control,
    name: 'data.media',
  });

  const fetchLot = async () => {
    setPageLoading(true);

    setStepData(null);
    // setLot(null);
    useLotStore.getState().setLot(null);
    setSelectedAgentType('');

    const { data } = await getLotById(id);

    if (data?.data) {
      // setLot(data.data);
      useLotStore.getState().setLot({ ...data.data });

      const [filtered] = data.data.processing_steps?.filter(step => step_id === step._id);
      setStepData(filtered);
    }

    setPageLoading(false);
  };

  useEffect(() => {
    fetchLot();
  }, [id, step_id]);

  useEffect(() => {
    if (!stepData || !lot) return;

    setPageLoading(true);

    if (stepData?.data?.agent_type) {
      setSelectedAgentType(stepData.data.agent_type);

      if (agentList && agentList[stepData.data.agent_type]) {
        const specifics = agentList[stepData.data.agent_type].map(agent => ({
          label: agent.agent_specifics,
          value: agent.agent_specifics,
          code: agent.code,
        }));
        setAgentSpecificList(specifics);
      } else {
        setAgentSpecificList([]);
      }
    } else {
      setSelectedAgentType('');
      setAgentSpecificList([]);
    }

    reset({
      sequence: stepData.sequence,
      name: stepData.name,
      status: stepData.status ?? '',
      data: {
        input_weight_kg: lot.weight_of_coffee ?? '',
        start_at: stepData?.data?.start_at ?? new Date(),
        end_at: stepData?.data?.end_at ?? new Date(),
        ...stepData?.data,
      },
    });
    console.log(getValues('data'));
    console.log(stepData?.data);
    
    setValue('data.agent_specific', stepData?.data?.agent_specific ?? '');
    console.log(getValues('data'));
    
    setIsFormReady(true);
    setPageLoading(false);
  }, [stepData, lot, agentList, reset, setValue]);

  // Effect to handle agentList loading
  useEffect(() => {
    if (selectedAgentType && agentList && agentList[selectedAgentType]) {
      const specifics = agentList[selectedAgentType].map(agent => ({
        label: agent.agent_specifics,
        value: agent.agent_specifics,
        code: agent.code,
      }));
      setAgentSpecificList(specifics);
    } else if (selectedAgentType && (!agentList || !agentList[selectedAgentType])) {
      setAgentSpecificList([]);
    }
  }, [selectedAgentType, agentList]);

  const onSaveDetails = async data => {
    setLoading(true);
    try {
      if (mediaToRemove.length > 0) {
        await deleteMedia(mediaToRemove.map(m => m.path));
        setMediaToRemove([]);
      }

      if (uploadRef.current) {
        await uploadRef.current();
      }
      const uploaded = await media.uploadAll();

      const existingMedia = Array.isArray(data.data.media) ? data.data.media : [];

      const mergedMedia = uploaded && uploaded.length > 0 ? [...existingMedia, ...uploaded] : existingMedia;

      // Remove process_checks from data if it exists
      const { process_checks, ...dataWithoutChecks } = data?.data || {};
      const filteredData = {
        ...data,
        data: { ...dataWithoutChecks, media: mergedMedia },
      };

      const respData = await updateSingleLotProcessStep({
        data: filteredData,
        lot_id: id,
        process_step_id: stepData?._id,
      });

      if (respData?.data?.success) {
        toast.success('Details saved successfully.');
        media.setLocalMedia([]);
        media.setUploadedMedia([]);
        navigate(-1, { replace: true });
      }
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || 'Failed to save details');
    } finally {
      setLoading(false);
    }
  };

  const onRemoveMedia = file => {
    setMediaToRemove(prev => [...prev, file]);
    const index = fields.findIndex(m => m._id === file._id);

    if (index !== -1) {
      remove(index);
    }
  };

  if (pageLoading || !isFormReady) {
    return <Loader />;
  }

  return (
    <div className="flex flex-1 flex-col space-y-[18px] px-4 py-[18px]">
      <LotCard lot={lot} />

      <div className="flex flex-col">
        <div className="flex items-center justify-between px-1">
          <div className="mb-1 font-medium">{stepData?.sequence + '.' + stepData?.name}</div>
        </div>
        {/* Form */}
        <form
          onSubmit={e => {
            e.preventDefault();
            handleSubmit(onSaveDetails)();
          }}
        >
          <Card className="rounded-2xl border border-border shadow-sm">
            <CardHeader className="px-4 pb-3">
              <CardTitle className="text-base font-medium">Stage Data</CardTitle>
            </CardHeader>

            <CardContent className="flex flex-col gap-3 p-4 pt-0">
              {/* Fermentation Only Fields */}
              {stepData?.name === 'Fermentation' && (
                <div className="mb-1 flex flex-col gap-1">
                  <FormField
                    select
                    label="Aerobic"
                    name="data.aerobic"
                    control={control}
                    errors={errors}
                    required
                    placeholder="Select type"
                    disabled={loading}
                    options={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                    ]}
                  />

                  <FormField
                    select
                    label="Modality"
                    name="data.modality"
                    control={control}
                    errors={errors}
                    required
                    placeholder="Select modality"
                    disabled={loading}
                    options={[
                      { label: 'Fermentation tank', value: 'Fermentation tank' },
                      { label: 'Closed plastic drum', value: 'Closed plastic drum' },
                      { label: 'ecotact bags', value: 'ecotact bags' },
                      { label: 'Open tank', value: 'Open tank' },
                    ]}
                  />

                  <Controller
                    name="data.agent_type"
                    control={control}
                    rules={{ required: 'Agent type is required' }}
                    render={({ field, fieldState }) => {
                      // Get current agent_specific value
                      const currentAgentSpecific = control._formValues?.data?.agent_specific;

                      return (
                        <div className="space-y-1.5">
                          <Label className="mb-1.5 text-sm font-normal text-[hsl(var(--text-foreground-secondary))]">
                            Agent type<span className="">*</span>
                          </Label>
                          <Select
                            value={field.value || ''}
                            onValueChange={value => {
                              field.onChange(value);
                              setSelectedAgentType(value);
                              // Only reset agent specific if it's not already present
                              if (!currentAgentSpecific) {
                                setValue('data.agent_specific', '');
                              }
                            }}
                            disabled={loading}
                          >
                            <SelectTrigger
                              className={cn('rounded-[10px] bg-white', fieldState.error && 'border-red-500')}
                            >
                              <SelectValue placeholder="Select agent type" />
                            </SelectTrigger>
                            <SelectContent>
                              {agentTypeOptions.map(option => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {fieldState.error && <div className="text-xs text-red-500">{fieldState.error.message}</div>}
                        </div>
                      );
                    }}
                  />

                  <FormField
                    select
                    label="Agent specific"
                    name="data.agent_specific"
                    control={control}
                    errors={errors}
                    required
                    placeholder="Select agent specific"
                    disabled={loading || !selectedAgentType}
                    options={agentSpecificList}
                  />

                  <FormField
                    textarea
                    name="data.notes"
                    label={'Notes on fermentation agent'}
                    placeholder={'Type specifics'}
                    disabled={loading}
                    required
                    errors={errors}
                    register={register}
                  />

                  <FormField
                    textarea
                    name="data.recipe"
                    label={'Recipe'}
                    placeholder={'Add recipe'}
                    disabled={loading}
                    required
                    errors={errors}
                    register={register}
                  />
                </div>
              )}

              {/* Available for all stages */}
              {/* Start At */}
              <FormField
                datetime
                name="data.start_at"
                control={control}
                disabled={loading}
                errors={errors}
                required
                label={
                  stepData?.name === 'Fermentation'
                    ? 'Fermentation start time'
                    : `Start of ${stepData?.name.toLowerCase()}`
                }
              />

              {stepData?.name === 'Fermentation' && (
                <div className="flex flex-col gap-3">
                  {/* Start At */}
                  <FormField
                    datetime
                    name="data.end_at"
                    control={control}
                    disabled={loading}
                    errors={errors}
                    required
                    label={
                      stepData?.name === 'Fermentation'
                        ? 'Fermentation end time'
                        : `end of ${stepData?.name.toLowerCase()}`
                    }
                  />

                  {/* Output weight of coffee */}
                  <FormField
                    name="data.output_weight_kg"
                    label="Output weight of coffee"
                    required
                    type="number"
                    inputMode="decimal"
                    placeholder="Enter weight"
                    step="0.01"
                    disabled={loading}
                    register={register}
                    errors={errors}
                  />
                </div>
              )}

              {stepData?.name !== 'Fermentation' && (
                <div className="flex flex-col gap-3">
                  {/* Input Weight of coffee */}
                  <FormField
                    label="Input weight of coffee (Kg)"
                    name="data.input_weight_kg"
                    required
                    type="number"
                    inputMode="decimal"
                    placeholder="Enter weight"
                    step="0.01"
                    disabled={loading}
                    register={register}
                    errors={errors}
                  />

                  {/* Output Weight of coffee */}
                  <FormField
                    name="data.output_weight_kg"
                    label="Output weight of coffee"
                    required
                    type="number"
                    inputMode="decimal"
                    placeholder="Enter weight"
                    step="0.01"
                    disabled={loading}
                    register={register}
                    errors={errors}
                  />

                  {/* Only Visible for Pulping */}
                  {/* Weight of skin */}
                  {stepData?.name === 'Pulping' && (
                    <FormField
                      label="Weight of skin"
                      name="data.weight_of_skin_kg"
                      required
                      type="number"
                      inputMode="decimal"
                      placeholder="Enter weight"
                      step="0.01"
                      disabled={loading}
                      register={register}
                      errors={errors}
                    />
                  )}

                  {/* End At */}
                  <FormField
                    datetime
                    name="data.end_at"
                    disabled={loading}
                    control={control}
                    errors={errors}
                    required
                    label={`End of ${stepData?.name.toLowerCase()}`}
                  />
                </div>
              )}

              {/* Media Inputs */}
              <MediaUploader
                media={media}
                label="Photos/Videos"
                folder={'lot-process-media'}
                onUploaded={fn => (uploadRef.current = fn)}
                className="bg-transparent"
                loading={loading}
              >
                {fields?.map((m, idx) => (
                  <div key={idx} className="relative h-[72px] w-[72px] overflow-hidden rounded-xl">
                    {m.mime_type === 'image' ? (
                      <img src={m.public_url} className="h-full w-full object-cover" alt="" />
                    ) : m.mime_type === 'video' ? (
                      <video src={m.public_url} className="h-full w-full object-cover" muted playsInline />
                    ) : null}
                    <button
                      type="button"
                      onClick={() => onRemoveMedia(m)}
                      className="absolute right-1 top-1 rounded-full bg-white/80 p-0.5"
                      disabled={loading}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </MediaUploader>

              <div className="mt-4 flex flex-col gap-2">
                <Button type="submit" disabled={loading}>
                  Save details
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
};

export default UpdateProcessStageInformationPage;
